import React, { ChangeEventHandler, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse } from "qs";

import { Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getIndicatedAddress } from "@/redux/selectors/location";
import { leaflyShopStringify } from "@/utils/qs";

import SearchInput from "@/components/SearchInput";
import TrackImpression from "@/components/TrackImpression";

import { FULFILLMENT_LABEL_MAP } from "../FulfillmentFilters/FulfillmentFilters";
import { EXCLUDED_CATEGORIES } from "../UnifiedShop";
import { extractFilterValues } from "../utils/filterUtils";

export const createHeaderString = (
  selectedCategories: string[] | null,
  selectedFulfillments: string[] | null,
  indicatedAddress: string | null,
  hasLocationFilter?: boolean,
) => {
  const assembledHeader = [];

  const showTHCandCBDInHeader =
    selectedCategories?.length === 1 && !selectedFulfillments
      ? !EXCLUDED_CATEGORIES.includes(selectedCategories[0])
      : false;

  // Category portion of header string
  if (selectedCategories) {
    assembledHeader.push(
      `Best ${showTHCandCBDInHeader ? "THC and CBD " : ""}${selectedCategories.map((c) => c.toLowerCase()).join(" and ")}`,
    );
  } else {
    assembledHeader.push("Shop cannabis products");
  }

  const hasValidFulfillmentValues =
    selectedFulfillments &&
    selectedFulfillments.every((f) => FULFILLMENT_LABEL_MAP[f]);

  // Fulfillment portion of header string
  if (hasValidFulfillmentValues) {
    assembledHeader.push(
      `for ${selectedFulfillments
        .map((c) => FULFILLMENT_LABEL_MAP[c].toLowerCase())
        .join(" or ")}`,
    );
  }

  // Location portion of header string
  if (hasLocationFilter) {
    assembledHeader.push(`near ${indicatedAddress}`);
  } else {
    assembledHeader.push("near me");
  }

  return assembledHeader.join(" ");
};

const Header: React.FC = () => {
  const { publishEvent } = useEventTracker();
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const parsedQs = parse(searchParams?.toString() || "");
  const indicatedAddress = useSelector(getIndicatedAddress);
  const { q: _q, page: _page, location, ...other } = parsedQs;
  const hasLocationFilter = Boolean(location);
  const query = parsedQs.q as string;
  const { selectedCategories, selectedFulfillments } =
    extractFilterValues(parsedQs);

  const attemptedRetailerName = parsedQs.attemptedRetailerName;

  const setQuery = (value?: string) => {
    const newQueryString = leaflyShopStringify({
      ...other,
      q: value,
    });
    push(`${pathname}${newQueryString ? `?${newQueryString}` : ""}`);
  };

  // * Local component state for input
  const [inputValue, setInputValue] = useState(query || "");
  // * Used to track the initial q parameter on page load
  const [initialSearch, _] = useState(query);

  const triggerSearch = (value: string) => {
    publishEvent({
      action: "click",
      category: Category.unifiedShoppingExperience,
      label: "search bar",
      searchedFor: value,
    });
    setQuery(value);
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    // * Sync local input state with quey param, if the query param
    // * is updated elsewhere.
    setInputValue(query || "");
  }, [query]);

  const headerString = attemptedRetailerName
    ? `No results for ${attemptedRetailerName}. Shop from these nearby retailers near ${indicatedAddress}`
    : createHeaderString(
        selectedCategories,
        selectedFulfillments,
        indicatedAddress,
        hasLocationFilter,
      );

  return (
    <div className="mb-xl">
      <div className="container mt-xl">
        <TrackImpression
          category={Category.unifiedShoppingExperience}
          label="search button"
          customDimensions={{
            default_option: initialSearch,
          }}
        >
          <SearchInput
            inputProps={{
              className: "h-12",
              onChange: changeHandler,
              onKeyDown: (e) => {
                if (e.key && e.key !== "Enter") {
                  return;
                }

                triggerSearch(inputValue);
              },
              placeholder: "Search products",
              value: inputValue,
            }}
            clearButtonProps={{
              onClick: () => {
                setInputValue("");
                setQuery("");
              },
            }}
            searchButtonProps={{
              onClick: () => triggerSearch(inputValue),
            }}
          />
        </TrackImpression>
        <h1 className="mt-lg text-md">{headerString}</h1>
      </div>
    </div>
  );
};

export default Header;
